import React, { useEffect, useState } from "react";
import { Row, Col, Card, Table, Button, Input, Modal, Typography, Tag, Space, Drawer, Form, Select, notification } from "antd";
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../config";

const { confirm } = Modal;
const { Item } = Form;
const { Option } = Select;

function PopTehsilsList() {
  const [form] = Form.useForm();
  const [dataList, setDataList]       = useState([]);
  const [totalCount, setTotalCount]   = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit]       = useState(15);
  const [search, setSearch]           = useState('');
  const [loading, setLoading]         = useState(false);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [editId, setEditId]           = useState(null);
  const [saving, setSaving]           = useState(false);

  // Filter state (State → District)
  const [filterStates, setFilterStates]     = useState([]);
  const [filterDistricts, setFilterDistricts] = useState([]);
  const [filterState, setFilterState]       = useState('');
  const [filterDistrict, setFilterDistrict] = useState('');

  // Drawer cascading
  const [countries, setCountries]         = useState([]);
  const [drawerStates, setDrawerStates]   = useState([]);
  const [drawerDistricts, setDrawerDistricts] = useState([]);

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };

  useEffect(() => {
    // Load all states for filter bar
    axios.get(`${baseUrl}/pop-states/list-all`, { headers: authHeader })
      .then(r => { if (r.data.status) setFilterStates(r.data.result); });
    // Load countries for drawer
    axios.get(`${baseUrl}/pop-country/list-all`, { headers: authHeader })
      .then(r => { if (r.data.status) setCountries(r.data.result); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchList = (page = currentPage, q = search, stateId = filterState, districtId = filterDistrict) => {
    setLoading(true);
    const params = { page, limit, search: q };
    if (stateId)    params.state_id    = stateId;
    if (districtId) params.district_id = districtId;
    axios.get(`${baseUrl}/pop-tehsils`, { headers: authHeader, params })
      .then(r => {
        if (r.data.status) {
          setDataList(r.data.result.map((item, i) => ({ ...item, key: (page - 1) * limit + i + 1 })));
          setTotalCount(r.data.totalCount);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchList(); }, [currentPage]); // eslint-disable-line

  const onFilterStateChange = (v) => {
    setFilterState(v || '');
    setFilterDistrict('');
    setFilterDistricts([]);
    setCurrentPage(1);
    fetchList(1, search, v || '', '');
    if (v) {
      axios.get(`${baseUrl}/pop-districts/list-all`, { headers: authHeader, params: { state_id: v } })
        .then(r => { if (r.data.status) setFilterDistricts(r.data.result); });
    }
  };

  const onFilterDistrictChange = (v) => {
    setFilterDistrict(v || '');
    setCurrentPage(1);
    fetchList(1, search, filterState, v || '');
  };

  const handleReset = () => {
    setFilterState(''); setFilterDistrict(''); setFilterDistricts([]);
    setSearch(''); setCurrentPage(1);
    fetchList(1, '', '', '');
  };

  // Drawer helpers
  const onDrawerCountryChange = (v) => {
    form.setFieldsValue({ state_id: undefined, district_id: undefined });
    setDrawerStates([]); setDrawerDistricts([]);
    if (v) axios.get(`${baseUrl}/pop-states/list-all`, { headers: authHeader, params: { country_id: v } })
      .then(r => { if (r.data.status) setDrawerStates(r.data.result); });
  };
  const onDrawerStateChange = (v) => {
    form.setFieldsValue({ district_id: undefined });
    setDrawerDistricts([]);
    if (v) axios.get(`${baseUrl}/pop-districts/list-all`, { headers: authHeader, params: { state_id: v } })
      .then(r => { if (r.data.status) setDrawerDistricts(r.data.result); });
  };

  const openAdd = () => {
    form.resetFields(); form.setFieldsValue({ status: '1' });
    setDrawerStates([]); setDrawerDistricts([]); setEditId(null); setDrawerOpen(true);
  };

  const openEdit = async (r) => {
    setDrawerStates([]); setDrawerDistricts([]); setEditId(r.id); setDrawerOpen(true);
    if (r.country_id) {
      const res = await axios.get(`${baseUrl}/pop-states/list-all`, { headers: authHeader, params: { country_id: r.country_id } });
      if (res.data.status) setDrawerStates(res.data.result);
    }
    if (r.state_id) {
      const res = await axios.get(`${baseUrl}/pop-districts/list-all`, { headers: authHeader, params: { state_id: r.state_id } });
      if (res.data.status) setDrawerDistricts(res.data.result);
    }
    form.setFieldsValue({
      country_id:  r.country_id  ? parseInt(r.country_id)  : undefined,
      state_id:    r.state_id    ? parseInt(r.state_id)    : undefined,
      district_id: r.district_id ? parseInt(r.district_id) : undefined,
      name: r.name, status: String(r.status),
    });
  };

  const onSave = async (values) => {
    setSaving(true);
    try {
      if (editId) await axios.put(`${baseUrl}/pop-tehsils/${editId}`, values, { headers: authHeader });
      else        await axios.post(`${baseUrl}/pop-tehsils`, values, { headers: authHeader });
      notification.success({ message: editId ? 'Tehsil updated' : 'Tehsil added' });
      setDrawerOpen(false); fetchList();
    } catch (e) {
      notification.error({ message: e.response?.data?.message || 'Error' });
    } finally { setSaving(false); }
  };

  const handleDelete = (id) => confirm({
    title: 'Delete this tehsil?', icon: <ExclamationCircleOutlined />,
    okText: 'Yes, Delete', okType: 'danger', cancelText: 'Cancel',
    onOk: async () => {
      await axios.delete(`${baseUrl}/pop-tehsils/${id}`, { headers: authHeader });
      notification.success({ message: 'Deleted' }); fetchList();
    },
  });

  const columns = [
    { title: '#', dataIndex: 'key', key: 'key', width: 60 },
    { title: 'Tehsil Name', dataIndex: 'name', key: 'name' },
    { title: 'District', dataIndex: 'district_name', key: 'district_name' },
    { title: 'State', dataIndex: 'state_name', key: 'state_name' },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 90,
      render: s => <Tag color={String(s) === '1' ? 'green' : 'red'}>{String(s) === '1' ? 'Active' : 'Inactive'}</Tag> },
    { title: 'Actions', key: 'actions', fixed: 'right', width: 140,
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}
            style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Edit</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>Delete</Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col span={24}>
          <Card
            title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>Tehsils <Tag color="orange">{totalCount}</Tag></span>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              <Button icon={<PlusOutlined />} onClick={openAdd}
                style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', fontWeight: 600 }}>
                Add Tehsil
              </Button>
            }
          >
            {/* ── Filter Bar ── */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
              <Space wrap size={10}>
                <Select placeholder="Filter by State" style={{ width: 180 }} allowClear showSearch optionFilterProp="children"
                  value={filterState || undefined} onChange={onFilterStateChange}>
                  {filterStates.map(s => <Option key={s.id} value={String(s.id)}>{s.name}</Option>)}
                </Select>

                <Select placeholder="Filter by District" style={{ width: 180 }} allowClear showSearch optionFilterProp="children"
                  value={filterDistrict || undefined} onChange={onFilterDistrictChange}
                  disabled={!filterState && filterDistricts.length === 0}>
                  {filterDistricts.map(d => <Option key={d.id} value={String(d.id)}>{d.name}</Option>)}
                </Select>

                <Input.Search
                  placeholder="Search tehsil name..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onSearch={val => { setSearch(val); setCurrentPage(1); fetchList(1, val, filterState, filterDistrict); }}
                  allowClear style={{ width: 240 }}
                  enterButton={<Button icon={<SearchOutlined />} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Search</Button>}
                />

                <Button icon={<ReloadOutlined />} onClick={handleReset}>Reset</Button>
              </Space>
            </div>

            <Table
              columns={columns} dataSource={dataList} loading={loading} size="small"
              scroll={{ x: 'max-content' }}
              pagination={{ current: currentPage, pageSize: limit, total: totalCount, onChange: p => setCurrentPage(p), showTotal: t => `Total ${t} tehsils`, showSizeChanger: false }}
            />
          </Card>
        </Col>
      </Row>

      <Drawer
        title={<span style={{ fontWeight: 700, color: '#1e293b' }}>{editId ? 'Edit Tehsil' : 'Add Tehsil'}</span>}
        width={520} open={drawerOpen} onClose={() => setDrawerOpen(false)} bodyStyle={{ paddingBottom: 80 }}
        footer={
          <Space>
            <Button loading={saving} onClick={() => form.submit()}
              style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Save</Button>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          <Item name="country_id" label="Country" rules={[{ required: true, message: 'Required' }]}>
            <Select placeholder="Select Country" showSearch optionFilterProp="children" onChange={onDrawerCountryChange} style={{ width: '100%' }}>
              {countries.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
            </Select>
          </Item>
          <Item name="state_id" label="State" rules={[{ required: true, message: 'Required' }]}>
            <Select placeholder="Select State" showSearch optionFilterProp="children" onChange={onDrawerStateChange} disabled={drawerStates.length === 0} style={{ width: '100%' }}>
              {drawerStates.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
            </Select>
          </Item>
          <Item name="district_id" label="District" rules={[{ required: true, message: 'Required' }]}>
            <Select placeholder="Select District" showSearch optionFilterProp="children" disabled={drawerDistricts.length === 0} style={{ width: '100%' }}>
              {drawerDistricts.map(d => <Option key={d.id} value={d.id}>{d.name}</Option>)}
            </Select>
          </Item>
          <Item name="name" label="Tehsil Name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="Enter tehsil name" />
          </Item>
          <Item name="status" label="Status">
            <Select><Option value="1">Active</Option><Option value="0">Inactive</Option></Select>
          </Item>
        </Form>
      </Drawer>
    </div>
  );
}

export default PopTehsilsList;
